import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { getOrgInfo, loadOrgTheme } from "@/theme";
import { WidgetPreview, DesignDrawer, CollapsibleSection } from "@/pages/Campaigns";

// Ensure selected Google fonts are loaded
function ensureGoogleFontLoaded(fontName){
  if(!fontName) return;
  const fam = String(fontName).trim();
  const id = `gf-${fam.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id; link.rel = 'stylesheet';
  const familyParam = fam.replace(/\s+/g, '+');
  link.href = `https://fonts.googleapis.com/css2?family=${familyParam}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

// Demo popup component - styled modal for the demo
function DemoPopup({ open, onClose, variant = "create" }) {
  if (!open) return null;
  
  const messages = {
    create: {
      title: "Functional Demo",
      description: "This is a functional demo - please get in touch if you'd like access to the platform.",
      icon: "🚀"
    },
    feature: {
      title: "Premium Feature",
      description: "This feature is only available on the paid platform. Contact us to get started!",
      icon: "✨"
    }
  };
  
  const msg = messages[variant] || messages.create;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <span>{msg.icon}</span>
            {msg.title}
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            {msg.description}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="sm:justify-center pt-4">
          <Button onClick={onClose} className="px-8">
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Embed Code Modal for the demo - identical to real one but copy triggers demo popup
function DemoEmbedCodeModal({ open, campaign, onClose, onCopyAttempt }){
  const [bg,setBg]=useState('#ffffff');
  const [width,setWidth]=useState('100%');
  const [maxWidth,setMaxWidth]=useState('600px');
  const [height,setHeight]=useState('700px');
  const [padding,setPadding]=useState('12px');
  const [hAlign,setHAlign]=useState('center');
  const [vAlign,setVAlign]=useState('top');
  
  if(!open || !campaign) return null;
  
  const base = typeof window!=='undefined'? window.location.origin: '';
  const src = `${base}/widget/${campaign.id}?outerBackground=${encodeURIComponent(bg)}&padding=${encodeURIComponent(padding)}`;
  
  const getContainerStyle = () => {
    const styles = [`background:${bg}`];
    styles.push('display:flex');
    if (hAlign === 'left') styles.push('justify-content:flex-start');
    else if (hAlign === 'right') styles.push('justify-content:flex-end');
    else styles.push('justify-content:center');
    if (vAlign === 'top') styles.push('align-items:flex-start');
    else if (vAlign === 'bottom') styles.push('align-items:flex-end');
    else styles.push('align-items:center');
    styles.push(`padding:${padding}`);
    return styles.join(';');
  };
  
  const containerStyle = getContainerStyle();
  const code = `<div style="${containerStyle}">
  <iframe src="${src}" style="width:${width};${maxWidth !== 'none' ? `max-width:${maxWidth};` : ''}height:${height};border:0;border-radius:8px;box-shadow:0 4px 6px -1px rgba(0,0,0,0.1);" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>
</div>`;

  const previewHClass = hAlign === 'left' ? 'justify-start' : hAlign === 'right' ? 'justify-end' : 'justify-center';
  const previewVClass = vAlign === 'top' ? 'items-start' : vAlign === 'bottom' ? 'items-end' : 'items-center';
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={(e)=>e.target===e.currentTarget && onClose()}>
      <div className="bg-white w-full max-w-4xl rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between sticky top-0">
          <div>
            <h2 className="text-white font-semibold text-lg">Embed Widget: {campaign.title}</h2>
            <p className="text-blue-100 text-sm">Add this widget to your website</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors p-1" type="button">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-5">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">iframe Size</h3>
              <div className="grid grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-xs text-gray-500 mb-1 block">Width</span>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" value={width} onChange={(e)=>setWidth(e.target.value)} placeholder="100%" />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500 mb-1 block">Max Width</span>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" value={maxWidth} onChange={(e)=>setMaxWidth(e.target.value)} placeholder="600px" />
                </label>
                <label className="block">
                  <span className="text-xs text-gray-500 mb-1 block">Height</span>
                  <input className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" value={height} onChange={(e)=>setHeight(e.target.value)} placeholder="700px" />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Background</h3>
                <div className="flex items-center gap-2">
                  <input type="color" value={bg} onChange={(e)=>setBg(e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border-2 border-gray-200" />
                  <input className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono bg-gray-50" value={bg} onChange={(e)=>setBg(e.target.value)} placeholder="#ffffff" />
                </div>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Padding</h3>
                <input className="w-full border rounded-lg px-3 py-2 text-sm bg-gray-50" value={padding} onChange={(e)=>setPadding(e.target.value)} placeholder="12px" />
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">Position</h3>
              <div className="space-y-3">
                <div>
                  <span className="text-xs text-gray-500 mb-2 block">Horizontal</span>
                  <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                    {[['left','Left'],['center','Center'],['right','Right']].map(([val,label])=>(
                      <button type="button" key={val} onClick={()=>setHAlign(val)} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${hAlign===val?'bg-white shadow text-blue-600':'text-gray-600 hover:text-gray-900'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-500 mb-2 block">Vertical</span>
                  <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                    {[['top','Top'],['middle','Middle'],['bottom','Bottom']].map(([val,label])=>(
                      <button type="button" key={val} onClick={()=>setVAlign(val)} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition ${vAlign===val?'bg-white shadow text-blue-600':'text-gray-600 hover:text-gray-900'}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Embed Code</h3>
              <textarea readOnly value={code} className="w-full h-28 border rounded-lg p-3 font-mono text-xs bg-gray-900 text-green-400 resize-none" />
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Preview</h3>
            <div className="border-2 border-dashed border-gray-200 rounded-xl overflow-hidden bg-gray-50 p-2">
              <div className="text-xs text-gray-400 mb-1 text-center">Your webpage</div>
              <div className={`flex ${previewHClass} ${previewVClass} rounded-lg border shadow-inner overflow-hidden transition-all duration-300`} style={{ height: '320px', backgroundColor: bg, padding: padding }}>
                <div className="bg-white rounded-lg shadow-lg flex flex-col overflow-hidden" style={{ width: '160px', minHeight: '280px' }}>
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-3 py-2 flex-shrink-0 rounded-t-lg">
                    <div className="w-16 h-2 bg-white/40 rounded mb-1"></div>
                    <div className="w-10 h-1.5 bg-white/25 rounded"></div>
                  </div>
                  <div className="p-2 space-y-1.5 bg-white flex-1 rounded-b-lg">
                    <div className="w-full h-5 bg-gray-100 rounded"></div>
                    <div className="w-full h-5 bg-gray-100 rounded"></div>
                    <div className="w-full h-8 bg-gray-100 rounded"></div>
                    <div className="w-full h-5 bg-blue-500 rounded mt-2"></div>
                  </div>
                </div>
              </div>
              <div className="text-center mt-2">
                <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-white px-2 py-1 rounded-full border">
                  Position: {hAlign} / {vAlign}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t sticky bottom-0">
          <div className="text-xs text-gray-500">
            Paste this code where you want the widget to appear.
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition">Close</button>
            <button type="button" onClick={onCopyAttempt} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition shadow-sm">
              Copy Embed Code
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


export default function FunctionalDemo() {
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showFeaturePopup, setShowFeaturePopup] = useState(false);
  const [embedModalOpen, setEmbedModalOpen] = useState(false);
  const [embedCampaign, setEmbedCampaign] = useState(null);
  const [drawerPanel, setDrawerPanel] = useState(null);
  const [sections, setSections] = useState({ setup: true, recipients: false, placeholders: false });
  
  // Full form state - identical to Campaigns.jsx
  const [form, setForm] = useState({ 
    campaign_type: "set_and_send",
    title: "", 
    subtitle: "",
    show_subtitle: false,
    description: "", 
    example_email: "", 
    talking_points: "", 
    recipients: "", 
    data_collection: false, 
    require_email: false, 
    require_name: false, 
    personalisation: true, 
    input_placeholders: { name: 'Your name', email: 'Your email', phone: 'Your phone number', personalisation: 'Optional personalisation (one or two sentences)' }, 
    theme: { show_logo: true, show_title: true, font: 'Inter', font_title: 'Inter', primary: '#0a2a66', primaryText: '#ffffff', secondary: '#eef2ff', fieldText: '#0a0a0a', fieldBorder: '#d1d5db', titleColor: '#0a0a0a', bg: '#ffffff', text: '#000000', scrollbarColor: '#0a2a66', buttonColor: '#eef2ff' },
    mp_ping_config: { scope: "national", target_level: "federal", target_state: "", house_filter: "both", include_federal_for_state: false, selected_senators: {}, always_include_emails: "" },
    sign_send_config: { show_counter: true, counter_position: "top", counter_display_mode: "count_only", signature_goal: 1000, show_counter_after: 0, unique_signatures_only: false, recipient_mode: "postcode", mp_level: "federal", set_recipients: "" },
    submission_config: { inquiry_name: "", preamble_address: "", committee_emails: "" },
    social_surge_config: { platforms: ["facebook", "x"], example_facebook_posts: "", facebook_include_link: false, facebook_link_url: "", x_post_type: "basic", example_tweets: "", x_quote_tweet_url: "" },
    button_style: { rounded: 'md', size: 'default', alignment: 'center' }
  });
  
  // Hardcoded demo campaigns — mix of published (active), draft (active), and
  // deactivated examples to surface every management action.
  const demoCampaigns = [
    {
      id: "demo-campaign-1",
      title: "Defend Free Speech in Australia",
      status: "published",
      active: true,
      campaign_type: "set_and_send",
      stats: { sent: 88, leads: 76 }
    },
    {
      id: "demo-campaign-2",
      title: "Protect Our Local Hospital — DRAFT",
      status: "draft",
      active: true,
      campaign_type: "sign_and_send",
      stats: { sent: 0, leads: 0 }
    }
  ];
  const inactiveDemoCampaigns = [
    {
      id: "demo-campaign-3",
      title: "Summer Petition (archived)",
      status: "published",
      active: false,
      campaign_type: "set_and_send",
      stats: { sent: 412, leads: 388 }
    }
  ];
  
  // Load org theme on mount
  useEffect(() => {
    loadOrgTheme().catch(() => {});
  }, []);
  
  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto" data-testid="functional-demo-page">
      {/* Create Campaign Form - identical structure to real Campaigns page */}
      <Card data-testid="demo-create-form">
        <CardHeader>
          <CardTitle>Create Campaign</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium mb-1 block">Campaign Type</label>
                <select 
                  className="border rounded px-3 py-2 w-full" 
                  value={form.campaign_type} 
                  onChange={(e)=>setForm({...form, campaign_type: e.target.value})}
                >
                  <option value="set_and_send">Quick Send Campaign</option>
                  <option value="mp_ping">MP Ping Campaign</option>
                  <option value="sign_and_send">Sign & Send Campaign</option>
                  <option value="submission_builder">Submission Builder</option>
                  <option value="social_surge">Social Surge Campaign</option>
                </select>
                <div className="text-xs text-gray-500 mt-1">
                  {form.campaign_type === "set_and_send" ? 
                    "Traditional campaign with pre-defined recipients" : 
                   form.campaign_type === "submission_builder" ?
                    "Help users write formal submissions to parliamentary committees" :
                   form.campaign_type === "social_surge" ?
                    "Mobilize supporters to share messages on Facebook and/or X" :
                   form.campaign_type === "sign_and_send" ?
                    "Petition-style campaign - users sign then send an AI email" :
                    "Dynamic campaign - users enter postcode to find their local MPs"}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 flex items-center gap-2">
                  Display Title
                  <span className="cursor-help text-gray-400 hover:text-gray-600" title="The main heading shown at the top of your campaign widget">ⓘ</span>
                </label>
                <Input placeholder="Enter your campaign title" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} />
              </div>
              
              <div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.show_subtitle} onChange={(e)=>setForm({...form, show_subtitle: e.target.checked})} />
                  <span>Add subtitle</span>
                  <span className="cursor-help text-gray-400 hover:text-gray-600" title="Display a subtitle below your main title">ⓘ</span>
                </label>
                {form.show_subtitle && (
                  <Input className="mt-2" placeholder="Enter subtitle text" value={form.subtitle} onChange={(e)=>setForm({...form,subtitle:e.target.value})} />
                )}
              </div>

              {/* Collapsible: Campaign Set Up */}
              <CollapsibleSection title="Campaign Set Up" isOpen={sections.setup} onToggle={() => setSections({...sections, setup: !sections.setup})}>
              <div>
                <label className="text-sm font-medium mb-1 flex items-center gap-2">
                  Description
                  <span className="cursor-help text-gray-400 hover:text-gray-600" title="Provide the AI with a brief explanation of what the campaign's aims are">ⓘ</span>
                </label>
                <Textarea placeholder="Describe your campaign" value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} />
              </div>
              
              {form.campaign_type !== "social_surge" && (
                <div>
                  <label className="text-sm font-medium mb-1 flex items-center gap-2">
                    Write an Example Email
                    <span className="cursor-help text-gray-400 hover:text-gray-600" title="A sample email that will be used as a template">ⓘ</span>
                  </label>
                  <Textarea placeholder="Write your example email here" value={form.example_email} onChange={(e)=>setForm({...form,example_email:e.target.value})} />
                </div>
              )}
              
              {form.campaign_type !== "social_surge" && (
                <div>
                  <label className="text-sm font-medium mb-1 flex items-center gap-2">
                    Talking Points
                    <span className="cursor-help text-gray-400 hover:text-gray-600" title="Key points or arguments to include. Enter one per line">ⓘ</span>
                  </label>
                  <Textarea placeholder="Enter talking points (one per line)" value={form.talking_points} onChange={(e)=>setForm({...form,talking_points:e.target.value})} />
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.data_collection} onChange={(e)=>setForm({...form,data_collection:e.target.checked})} /> Data collection</label>
                {form.campaign_type !== "submission_builder" && (
                  <>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={form.require_email} onChange={(e)=>setForm({...form,require_email:e.target.checked})} /> Require email</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={form.require_name} onChange={(e)=>setForm({...form,require_name:e.target.checked})} /> Require name</label>
                  </>
                )}
              </div>
              </CollapsibleSection>

              {/* Collapsible: Recipient Settings */}
              <CollapsibleSection title="Recipient Settings" isOpen={sections.recipients} onToggle={() => setSections({...sections, recipients: !sections.recipients})}>

              {/* Set & Send recipients */}
              {form.campaign_type === "set_and_send" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium mb-1 flex items-center gap-2">
                    Recipient Emails
                    <span className="cursor-help text-gray-400 hover:text-gray-600" title="Email addresses of recipients. Separate multiple emails with commas">ⓘ</span>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Textarea placeholder="Enter email addresses (comma-separated)" value={form.recipients} onChange={(e)=>setForm({...form,recipients:e.target.value})} />
                    <Button variant="secondary" onClick={() => setShowFeaturePopup(true)}>Pick from Directory</Button>
                  </div>
                </div>
              )}
              
              {/* MP Ping Configuration */}
              {form.campaign_type === "mp_ping" && (
                <div className="space-y-3 border rounded p-3 bg-blue-50">
                  <div className="font-medium text-sm text-blue-900">MP Ping Configuration</div>
                  
                  <div>
                    <label className="text-sm font-medium mb-1 block">Campaign Scope</label>
                    <select className="border rounded px-3 py-2 w-full bg-white" value={form.mp_ping_config.scope} onChange={(e)=>setForm({...form, mp_ping_config:{...form.mp_ping_config, scope:e.target.value}})}>
                      <option value="national">National (All States)</option>
                      <option value="state">State-Based</option>
                    </select>
                  </div>
                  
                  {form.mp_ping_config.scope === "state" && (
                    <div>
                      <label className="text-sm font-medium mb-1 block">Target State</label>
                      <select className="border rounded px-3 py-2 w-full bg-white" value={form.mp_ping_config.target_state} onChange={(e)=>setForm({...form, mp_ping_config:{...form.mp_ping_config, target_state:e.target.value}})}>
                        <option value="">Select State</option>
                        <option value="NSW">NSW</option>
                        <option value="VIC">VIC</option>
                        <option value="QLD">QLD</option>
                        <option value="SA">SA</option>
                        <option value="WA">WA</option>
                        <option value="TAS">TAS</option>
                        <option value="ACT">ACT</option>
                        <option value="NT">NT</option>
                      </select>
                    </div>
                  )}
                  
                  <div>
                    <label className="text-sm font-medium mb-1 block">Target MPs</label>
                    <select className="border rounded px-3 py-2 w-full bg-white" value={form.mp_ping_config.target_level} onChange={(e)=>setForm({...form, mp_ping_config:{...form.mp_ping_config, target_level:e.target.value}})}>
                      <option value="federal">Federal MPs</option>
                      <option value="state">State MPs</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-1 block">House Filter</label>
                    <select className="border rounded px-3 py-2 w-full bg-white" value={form.mp_ping_config.house_filter} onChange={(e)=>setForm({...form, mp_ping_config:{...form.mp_ping_config, house_filter:e.target.value}})}>
                      <option value="lower">Lower House Only {form.mp_ping_config.target_level === "federal" ? "(House of Reps)" : ""}</option>
                      <option value="both">Both Houses</option>
                      <option value="upper">Upper House Only {form.mp_ping_config.target_level === "federal" ? "(Senate)" : ""}</option>
                    </select>
                  </div>
                  
                  {form.mp_ping_config.target_level === "state" && form.mp_ping_config.scope === "state" && (
                    <div className="text-sm">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={form.mp_ping_config.include_federal_for_state} onChange={(e)=>setForm({...form, mp_ping_config:{...form.mp_ping_config, include_federal_for_state:e.target.checked}})} />
                        <span>Also include local Federal MP</span>
                      </label>
                    </div>
                  )}
                  
                  {(form.mp_ping_config.house_filter === "upper" || form.mp_ping_config.house_filter === "both") && (
                    <div className="border-t pt-3 mt-3">
                      <div className="text-sm font-medium mb-2">
                        Upper House Selection
                        <span className="text-xs text-red-600 ml-2">(Required)</span>
                      </div>
                      <Button type="button" size="sm" variant="outline" onClick={() => setShowFeaturePopup(true)}>
                        Select Specific {form.mp_ping_config.target_level === "federal" ? "Senators" : "MLCs"}
                      </Button>
                    </div>
                  )}
                  
                  <div className="border-t pt-3 mt-3">
                    <label className="text-sm font-medium mb-1 block">Always Include (Optional)</label>
                    <p className="text-xs text-gray-500 mb-2">Add MPs that will ALWAYS be contacted, regardless of the user's postcode.</p>
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowFeaturePopup(true)}>
                      Select from Directory
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Sign & Send Configuration */}
              {form.campaign_type === "sign_and_send" && (
                <div className="space-y-3 border rounded p-3 bg-purple-50">
                  <div className="font-medium text-sm text-purple-900">Sign & Send Configuration</div>
                  
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.sign_send_config.show_counter} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, show_counter:e.target.checked}})} />
                      <span>Show signature counter</span>
                    </label>
                    
                    {form.sign_send_config.show_counter && (
                      <div className="ml-6 space-y-2">
                        <div>
                          <label className="text-xs font-medium mb-1 block">Counter Display Mode</label>
                          <select className="w-full border rounded p-2 text-sm" value={form.sign_send_config.counter_display_mode} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, counter_display_mode:e.target.value}})}>
                            <option value="count_only">Count only (e.g., "1,234 signatures")</option>
                            <option value="progress">Progress towards goal (e.g., "1,234 of 5,000")</option>
                          </select>
                        </div>
                        
                        {form.sign_send_config.counter_display_mode === "progress" && (
                          <div>
                            <label className="text-xs font-medium mb-1 block">Signature Goal</label>
                            <Input type="number" min="1" placeholder="1000" value={form.sign_send_config.signature_goal} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, signature_goal:parseInt(e.target.value)||1000}})} />
                          </div>
                        )}
                        
                        <div>
                          <label className="text-xs font-medium mb-1 block">Counter Position</label>
                          <select className="w-full border rounded p-2 text-sm" value={form.sign_send_config.counter_position} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, counter_position:e.target.value}})}>
                            <option value="top">Top of widget</option>
                            <option value="bottom">Bottom of widget</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="text-xs font-medium mb-1 block">Show counter after (signatures)</label>
                          <Input type="number" min="0" placeholder="0" value={form.sign_send_config.show_counter_after} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, show_counter_after:parseInt(e.target.value)||0}})} />
                          <div className="text-xs text-gray-500 mt-1">Counter will only display after this many signatures (0 = always show)</div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.sign_send_config.unique_signatures_only} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, unique_signatures_only:e.target.checked}})} />
                      <span>Limit to one signature per email</span>
                    </label>
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Recipient Mode</label>
                    <div className="space-y-1">
                      <label className="flex items-center gap-2 text-sm">
                        <input type="radio" name="recipient_mode" value="postcode" checked={form.sign_send_config.recipient_mode === "postcode"} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, recipient_mode:e.target.value}})} />
                        <span>Contact local MP via postcode</span>
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input type="radio" name="recipient_mode" value="set_recipients" checked={form.sign_send_config.recipient_mode === "set_recipients"} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, recipient_mode:e.target.value}})} />
                        <span>Set recipient list</span>
                      </label>
                    </div>
                  </div>
                  
                  {form.sign_send_config.recipient_mode === "postcode" && (
                    <div>
                      <label className="text-sm font-medium mb-1 block">MP Level</label>
                      <select className="w-full border rounded p-2" value={form.sign_send_config.mp_level} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, mp_level:e.target.value}})}>
                        <option value="federal">Federal MP</option>
                        <option value="state">State MP</option>
                      </select>
                    </div>
                  )}
                  
                  {form.sign_send_config.recipient_mode === "set_recipients" && (
                    <div>
                      <label className="text-sm font-medium mb-1 block">Recipient Emails</label>
                      <Textarea placeholder="Enter email addresses (comma-separated)" value={form.sign_send_config.set_recipients} onChange={(e)=>setForm({...form, sign_send_config:{...form.sign_send_config, set_recipients:e.target.value}})} />
                    </div>
                  )}
                  
                  <div className="text-xs text-purple-700 bg-purple-100 p-2 rounded">
                    ℹ️ Users will sign the petition first, then send an AI-generated email.
                  </div>
                </div>
              )}
              
              {/* Submission Builder Configuration */}
              {form.campaign_type === "submission_builder" && (
                <div className="space-y-3 border rounded p-3 bg-amber-50">
                  <div className="font-medium text-amber-800">Submission Builder Configuration</div>
                  
                  <div>
                    <label className="text-sm font-medium mb-1 flex items-center gap-2">
                      Inquiry/Committee Name
                      <span className="cursor-help text-gray-400 hover:text-gray-600" title="The name of the inquiry or committee this submission is for">ⓘ</span>
                    </label>
                    <Input placeholder="e.g., Inquiry into WA Health Services" value={form.submission_config.inquiry_name} onChange={(e)=>setForm({...form, submission_config:{...form.submission_config, inquiry_name:e.target.value}})} />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-1 flex items-center gap-2">
                      Official Preamble Address
                      <span className="cursor-help text-gray-400 hover:text-gray-600" title="Select a parliament or enter a custom address">ⓘ</span>
                    </label>
                    <select className="w-full border rounded p-2 mb-2 text-sm" value="" onChange={(e)=>{ if (e.target.value) { setForm({...form, submission_config:{...form.submission_config, preamble_address:e.target.value}}); }}}>
                      <option value="">-- Select a Parliament --</option>
                      <optgroup label="Federal">
                        <option value={"Senate of Australia\nParliament House\nCANBERRA ACT 2600\nAUSTRALIA"}>Senate of Australia</option>
                        <option value={"House of Representatives\nParliament House\nCANBERRA ACT 2600\nAUSTRALIA"}>House of Representatives</option>
                        <option value={"Parliament of Australia\nParliament House\nCanberra ACT 2600\nAUSTRALIA"}>Parliament of Australia</option>
                      </optgroup>
                      <optgroup label="Western Australia">
                        <option value={"Legislative Assembly of Western Australia\nParliament House\n4 Harvest Terrace\nWEST PERTH WA 6005"}>Legislative Assembly of WA</option>
                        <option value={"Legislative Council of Western Australia\nParliament House\n4 Harvest Terrace\nWEST PERTH WA 6005"}>Legislative Council of WA</option>
                      </optgroup>
                      <optgroup label="New South Wales">
                        <option value={"New South Wales Parliament\nParliament House\n6 Macquarie Street\nSYDNEY NSW 2000"}>NSW Parliament</option>
                        <option value={"Legislative Assembly of New South Wales\nParliament House\n6 Macquarie Street\nSYDNEY NSW 2000"}>Legislative Assembly of NSW</option>
                        <option value={"Legislative Council of New South Wales\nParliament House\n6 Macquarie Street\nSYDNEY NSW 2000"}>Legislative Council of NSW</option>
                      </optgroup>
                      <optgroup label="Victoria">
                        <option value={"Parliament of Victoria\nParliament House\nSpring Street\nEAST MELBOURNE VIC 3002"}>Parliament of Victoria</option>
                        <option value={"Legislative Assembly of Victoria\nParliament House\nSpring Street\nEAST MELBOURNE VIC 3002"}>Legislative Assembly of Victoria</option>
                        <option value={"Legislative Council of Victoria\nParliament House\nSpring Street\nEAST MELBOURNE VIC 3002"}>Legislative Council of Victoria</option>
                      </optgroup>
                      <optgroup label="Queensland">
                        <option value={"Parliament of Queensland\nParliament House\nCnr George and Alice Streets\nBRISBANE QLD 4000"}>Parliament of Queensland</option>
                        <option value={"Legislative Assembly of Queensland\nParliament House\nCnr George and Alice Streets\nBRISBANE QLD 4000"}>Legislative Assembly of Queensland</option>
                      </optgroup>
                      <optgroup label="South Australia">
                        <option value={"Parliament of South Australia\nParliament House\nNorth Terrace\nADELAIDE SA 5000"}>Parliament of South Australia</option>
                        <option value={"House of Assembly of South Australia\nParliament House\nNorth Terrace\nADELAIDE SA 5000"}>House of Assembly of SA</option>
                        <option value={"Legislative Council of South Australia\nParliament House\nNorth Terrace\nADELAIDE SA 5000"}>Legislative Council of SA</option>
                      </optgroup>
                      <optgroup label="Tasmania">
                        <option value={"Parliament of Tasmania\nParliament House\nHobart TAS 7000"}>Parliament of Tasmania</option>
                        <option value={"House of Assembly of Tasmania\nParliament House\nHobart TAS 7000"}>House of Assembly of Tasmania</option>
                        <option value={"Legislative Council of Tasmania\nParliament House\nHobart TAS 7000"}>Legislative Council of Tasmania</option>
                      </optgroup>
                      <optgroup label="Territories">
                        <option value={"Parliament of the Australian Capital Territory\nACT Legislative Assembly\n196 London Circuit\nCANBERRA ACT 2601"}>ACT Legislative Assembly</option>
                        <option value={"Parliament of the Northern Territory\nLegislative Assembly of the Northern Territory\nParliament House\n15 Mitchell Street\nDARWIN NT 0800"}>NT Legislative Assembly</option>
                      </optgroup>
                    </select>
                    <Textarea placeholder={"Senate of Australia\nParliament House\nCANBERRA ACT 2600\nAUSTRALIA"} value={form.submission_config.preamble_address} onChange={(e)=>setForm({...form, submission_config:{...form.submission_config, preamble_address:e.target.value}})} rows={4} />
                    <div className="text-xs text-gray-500 mt-1">Select from dropdown or edit manually</div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium mb-1 flex items-center gap-2">
                      Committee Email(s)
                      <span className="cursor-help text-gray-400 hover:text-gray-600" title="Email address(es) of the committee. Maximum 2 emails, comma-separated">ⓘ</span>
                    </label>
                    <Input placeholder="committee@parliament.wa.gov.au" value={form.submission_config.committee_emails} onChange={(e)=>setForm({...form, submission_config:{...form.submission_config, committee_emails:e.target.value}})} />
                    <div className="text-xs text-gray-500 mt-1">Maximum 2 committee email addresses, comma-separated</div>
                  </div>
                  
                  <div className="text-xs text-amber-700 bg-amber-100 p-2 rounded">
                    ℹ️ Submission Builder requires Name, Email, and Phone Number from users. These fields will be automatically enabled.
                  </div>
                </div>
              )}
              
              {/* Social Surge Configuration */}
              {form.campaign_type === "social_surge" && (
                <div className="space-y-4 border rounded p-3 bg-purple-50">
                  <div className="font-medium text-purple-800">Social Surge Configuration</div>
                  
                  <div>
                    <label className="text-sm font-medium mb-2 block">Platforms</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.social_surge_config.platforms?.includes("facebook")} onChange={(e)=>{
                          const current = form.social_surge_config.platforms || [];
                          const updated = e.target.checked ? [...current.filter(p=>p!=="facebook"), "facebook"] : current.filter(p=>p!=="facebook");
                          setForm({...form, social_surge_config:{...form.social_surge_config, platforms: updated}});
                        }} />
                        <span className="text-sm">Facebook</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={form.social_surge_config.platforms?.includes("x")} onChange={(e)=>{
                          const current = form.social_surge_config.platforms || [];
                          const updated = e.target.checked ? [...current.filter(p=>p!=="x"), "x"] : current.filter(p=>p!=="x");
                          setForm({...form, social_surge_config:{...form.social_surge_config, platforms: updated}});
                        }} />
                        <span className="text-sm">X (Twitter)</span>
                      </label>
                    </div>
                  </div>
                  
                  {form.social_surge_config.platforms?.includes("facebook") && (
                    <div className="space-y-3 border-t pt-3">
                      <div className="font-medium text-sm text-blue-700">Facebook Settings</div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Example Facebook Posts</label>
                        <Textarea placeholder="Enter example Facebook posts for AI reference..." value={form.social_surge_config.example_facebook_posts} onChange={(e)=>setForm({...form, social_surge_config:{...form.social_surge_config, example_facebook_posts:e.target.value}})} rows={3} />
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={form.social_surge_config.facebook_include_link} onChange={(e)=>setForm({...form, social_surge_config:{...form.social_surge_config, facebook_include_link:e.target.checked}})} />
                        <span className="text-sm">Include a link with the share</span>
                      </div>
                      {form.social_surge_config.facebook_include_link && (
                        <div>
                          <label className="text-sm font-medium mb-1 block">Link URL</label>
                          <Input placeholder="https://example.com/article" value={form.social_surge_config.facebook_link_url} onChange={(e)=>setForm({...form, social_surge_config:{...form.social_surge_config, facebook_link_url:e.target.value}})} />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {form.social_surge_config.platforms?.includes("x") && (
                    <div className="space-y-3 border-t pt-3">
                      <div className="font-medium text-sm text-gray-800">X (Twitter) Settings</div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Post Type</label>
                        <select className="w-full border rounded p-2 text-sm" value={form.social_surge_config.x_post_type} onChange={(e)=>setForm({...form, social_surge_config:{...form.social_surge_config, x_post_type:e.target.value}})}>
                          <option value="basic">Basic Post (new tweet)</option>
                          <option value="quote_tweet">Quote Tweet (quote an existing tweet)</option>
                          <option value="reply">Reply (reply to an existing tweet)</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Example Tweets</label>
                        <Textarea placeholder="Enter example tweets for AI reference..." value={form.social_surge_config.example_tweets} onChange={(e)=>setForm({...form, social_surge_config:{...form.social_surge_config, example_tweets:e.target.value}})} rows={3} maxLength={280} />
                        <div className="text-xs text-gray-500 mt-1">AI will use these examples to generate tweets for users ({form.social_surge_config.example_tweets?.length || 0}/280 characters)</div>
                      </div>
                      {(form.social_surge_config.x_post_type === "quote_tweet" || form.social_surge_config.x_post_type === "reply") && (
                        <div>
                          <label className="text-sm font-medium mb-1 block">{form.social_surge_config.x_post_type === "quote_tweet" ? "Quote Tweet URL" : "Reply to Tweet URL"}</label>
                          <Input placeholder="e.g., https://x.com/user/status/1234567890123456789" value={form.social_surge_config.x_quote_tweet_url} onChange={(e)=>setForm({...form, social_surge_config:{...form.social_surge_config, x_quote_tweet_url:e.target.value}})} />
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="text-xs text-purple-700 bg-purple-100 p-2 rounded">
                    ℹ️ Social Surge campaigns don't require email generation. Users will share pre-written content on social media.
                  </div>
                </div>
              )}
              </CollapsibleSection>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.theme.show_logo} onChange={(e)=>setForm({...form, theme:{...form.theme, show_logo:e.target.checked}})} /> Show logo on widget</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.theme.show_title} onChange={(e)=>setForm({...form, theme:{...form.theme, show_title:e.target.checked}})} /> Show title text</label>
              </div>

              {/* Design moved to the floating drawer — click any element in the
                  preview, or the button below, to open it. */}
              <button
                type="button"
                data-testid="demo-open-design"
                onClick={() => setDrawerPanel('brand')}
                className="w-full border border-dashed border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-700 hover:border-[#1E3A8A] hover:bg-[#1E3A8A]/5 hover:text-[#1E3A8A] transition flex items-center justify-between"
              >
                <span><span className="font-medium">Design</span> — fonts, colours, logo, title, fields, button &amp; background</span>
                <span className="text-xs opacity-70">Open ✎</span>
              </button>

              {/* Collapsible: Placeholder Text */}
              <CollapsibleSection title="Placeholder Text" isOpen={sections.placeholders} onToggle={() => setSections({...sections, placeholders: !sections.placeholders})}>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Name placeholder" value={form.input_placeholders?.name || ''} onChange={(e)=>setForm({...form, input_placeholders:{...form.input_placeholders, name:e.target.value}})} />
                  <Input placeholder="Email placeholder" value={form.input_placeholders?.email || ''} onChange={(e)=>setForm({...form, input_placeholders:{...form.input_placeholders, email:e.target.value}})} />
                  <Input className="col-span-2" placeholder="Personalisation placeholder" value={form.input_placeholders?.personalisation || ''} onChange={(e)=>setForm({...form, input_placeholders:{...form.input_placeholders, personalisation:e.target.value}})} />
                </div>
              </CollapsibleSection>

              <Button onClick={() => setShowCreatePopup(true)}>Create</Button>
            </div>
            
            {/* Widget Preview */}
            <WidgetPreview 
              title={form.title} 
              subtitle={form.subtitle} 
              showSubtitle={form.show_subtitle} 
              theme={form.theme} 
              requireEmail={form.require_email} 
              requireName={form.require_name} 
              personalise={form.personalisation} 
              dataCollection={form.data_collection} 
              recipients={form.recipients} 
              inputPlaceholders={form.input_placeholders} 
              campaignType={form.campaign_type} 
              signSendConfig={form.sign_send_config} 
              submissionConfig={form.submission_config} 
              socialSurgeConfig={form.social_surge_config}
              buttonStyle={form.button_style}
              onPatch={(field, value) => setForm(prev => ({ ...prev, [field]: value, ...(field === 'subtitle' && value ? { show_subtitle: true } : {}) }))}
              onButtonStyleChange={(bs) => setForm(prev => ({ ...prev, button_style: bs }))}
              onConfigPatch={(cfg, key, value) => setForm(prev => ({ ...prev, [cfg]: { ...(prev[cfg] || {}), [key]: value } }))}
              onOpenDrawer={(panel) => setDrawerPanel(panel)}
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Active Campaigns */}
      <Card>
        <CardHeader>
          <CardTitle>Active Campaigns ({demoCampaigns.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {demoCampaigns.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.title}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${c.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{c.status}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        {c.status === 'draft' && (
                          <Button size="sm" data-testid={`demo-publish-${c.id}`} onClick={() => setShowFeaturePopup(true)}>Publish</Button>
                        )}
                        <Button size="sm" variant="secondary" onClick={() => setShowFeaturePopup(true)}>Edit</Button>
                        <Button size="sm" variant="outline" onClick={() => setShowFeaturePopup(true)}>Analytics</Button>
                        <Button size="sm" variant="outline" onClick={() => setShowFeaturePopup(true)}>Open widget</Button>
                        <Button size="sm" variant="outline" onClick={() => { setEmbedCampaign(c); setEmbedModalOpen(true); }}>Copy Embed Code</Button>
                        <Button size="sm" variant="outline" onClick={() => setShowFeaturePopup(true)}>Deactivate</Button>
                        <span className="ml-2 border-l pl-3 flex gap-2 text-xs text-gray-500">
                          <span title="Sent">📧 {c.stats.sent}</span>
                          <span title="Leads">👤 {c.stats.leads}</span>
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Inactive (archived) campaigns — mirrors the real management surface */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Inactive Campaigns ({inactiveDemoCampaigns.length})</span>
            <span className="text-xs text-gray-500 font-normal">Re-activate or permanently delete archived campaigns</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inactiveDemoCampaigns.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-gray-600">{c.title}</TableCell>
                    <TableCell className="text-gray-600">{c.status}</TableCell>
                    <TableCell className="space-x-2">
                      <Button size="sm" variant="secondary" onClick={() => setShowFeaturePopup(true)}>Activate</Button>
                      <Button size="sm" variant="outline" onClick={() => setShowFeaturePopup(true)}>Analytics</Button>
                      <Button size="sm" variant="destructive" onClick={() => setShowFeaturePopup(true)}>Delete</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      
      {/* Demo Popups */}
      <DemoPopup open={showCreatePopup} onClose={() => setShowCreatePopup(false)} variant="create" />
      <DemoPopup open={showFeaturePopup} onClose={() => setShowFeaturePopup(false)} variant="feature" />
      
      {/* Embed Code Modal */}
      <DemoEmbedCodeModal
        open={embedModalOpen}
        campaign={embedCampaign}
        onClose={() => setEmbedModalOpen(false)}
        onCopyAttempt={() => {
          setEmbedModalOpen(false);
          setShowFeaturePopup(true);
        }}
      />

      {/* Design drawer — context-sensitive style controls (same as Campaigns) */}
      <DesignDrawer
        open={!!drawerPanel}
        panel={drawerPanel}
        onClose={() => setDrawerPanel(null)}
        theme={form.theme}
        onChange={Object.assign(
          (newTheme) => setForm(prev => ({ ...prev, theme: newTheme })),
          { __setPanel: (p) => setDrawerPanel(p) }
        )}
        buttonStyle={form.button_style}
        onButtonStyleChange={(bs) => setForm(prev => ({ ...prev, button_style: bs }))}
        subtitle={form.subtitle}
        showSubtitle={!!form.show_subtitle}
        onPatch={(field, value) => setForm(prev => ({ ...prev, [field]: value, ...(field === 'subtitle' && value ? { show_subtitle: true } : {}) }))}
      />
    </div>
  );
}
